import { useEffect, useMemo, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useGetMyHatimInstanceGroupsQuery } from "../../../api/hatimManagementApi";
import { assignedLabel, buildProgressView, sortMembers } from "../../../lib/hatimProgress";
import { format } from "../../../translations";
import { SoftBar, cls, progressColor, useIsDark } from "../../hatim/soft/SoftKit";
import ConnectionState from "../../../components/ui/ConnectionState";

const TR_MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${TR_MONTHS[d.getMonth()]}`;
}

function initials(m) {
  return (((m.name || "?")[0] || "") + ((m.surname || "")[0] || "")).toUpperCase();
}


/**
 * Hatim detayı → İlerleme sekmesi. Mobil "İlerlemeler → Hatimler → hatim"
 * ekranıyla aynı düzen ve aynı veri kaynağı (get-my-hatim-instance-groups):
 * dönem seçimi, ilerleme durumu kartı, üye kartları. Hesap istemcide.
 */
export default function HatimProgressList({ hatimTemplateId }) {
  const { t } = useScriptMode();
  const dark = useIsDark();
  const [instanceId, setInstanceId] = useState(null);

  // Hatim değişince dönem seçimi sıfırlansın (aktif döneme dönsün).
  useEffect(() => {
    setInstanceId(null);
  }, [hatimTemplateId]);

  // Üyeler mobilden ilerleme girdikçe liste kendi kendine tazelensin:
  // 30 sn'de bir yokla; sekmeye dönüşte ayrıca refetchOnFocus devreye girer.
  const { data, isLoading, isFetching, isError, error, refetch } = useGetMyHatimInstanceGroupsQuery(
    undefined,
    { skip: !hatimTemplateId, pollingInterval: 30000 }
  );
  const view = useMemo(
    () => buildProgressView(data?.data?.groups, hatimTemplateId, instanceId),
    [data, hatimTemplateId, instanceId]
  );
  const { instance, summary, periods } = view;
  const members = useMemo(() => sortMembers(view.members), [view.members]);
  const rangeBased = view.isRangeType;
  const unit = rangeBased ? t("hatim_progress_unit_pages") : t("hatim_progress_unit_count");

  if (isLoading) {
    // Gerçek düzenin iskeleti: dönem çipleri, durum şeridi, üye satırları.
    const skSx = { bgcolor: dark ? "rgba(255,255,255,0.12)" : "#D6EBEB" };
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={1}>
          <Skeleton variant="rounded" width={132} height={52} sx={skSx} />
          <Skeleton variant="rounded" width={132} height={52} sx={skSx} />
        </Box>
        <Skeleton variant="rounded" height={72} sx={skSx} />
        {[0, 1, 2].map((i) => (
          <Box key={i} display="flex" alignItems="center" gap={1.5}>
            <Skeleton variant="circular" width={36} height={36} sx={skSx} />
            <Box flex={1}>
              <Skeleton variant="text" width="45%" sx={skSx} />
              <Skeleton variant="rounded" height={4} sx={skSx} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (isError) {
    return (
      <ConnectionState
        error={error}
        onRetry={refetch}
        retrying={isFetching}
        message={t("hatim_progress_load_error")}
        compact
      />
    );
  }

  if (!instance) {
    return <p className={`text-sm ${cls.muted}`}>{t("hatim_progress_no_instance")}</p>;
  }

  const percent = Math.min(100, Math.round(summary?.percent || 0));
  const completed = percent >= 100;
  const totalLabel = summary?.totalAmount || summary?.totalAssigned || 0;

  return (
    <div className={`flex flex-col gap-4 lg:flex-1 lg:min-h-0 ${isFetching ? "opacity-70" : ""}`}>
      {periods.length > 0 && (
        <section className="shrink-0">
          <h4 className={`text-xs uppercase tracking-wide mb-2 ${cls.muted}`}>{t("hatim_progress_period_select")}</h4>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {periods.map((p) => {
              const selected = String(p.id) === String(instance.id);
              const pct = Math.min(100, Math.round(p.percent || 0));
              const color = progressColor(pct, dark);
              return (
                <button
                  key={String(p.id)}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setInstanceId(String(p.id))}
                  className={[
                    "shrink-0 min-w-[132px] text-left rounded-xl px-3 py-2 transition-colors",
                    selected
                      ? "bg-terakkiPrimaryDark text-white dark:bg-white dark:text-terakkiPrimaryDark"
                      : "bg-terakkiBrandCloud text-terakkiInk hover:bg-terakkiBrandMist dark:bg-white/10 dark:text-white dark:hover:bg-white/[0.16]",
                  ].join(" ")}
                >
                  <div className="text-sm font-Medium">
                    {format(t("hatim_progress_period"), { n: p.currentPeriod })}
                    {p.active ? ` (${t("hatim_progress_period_active")})` : ""}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <SoftBar pct={pct} color={color} />
                    <span className="text-xs font-Medium" style={{ color: selected ? undefined : color }}>
                      %{pct}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Durum: açık şerit, ince çubuk, yüzde */}
      <section className={`shrink-0 rounded-xl px-4 py-3 ${cls.summary} border-b-0`}>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className={`text-xs uppercase tracking-wide ${cls.muted}`}>{t("hatim_progress_status_title")}</h4>
          {summary?.unassigned > 0 ? (
            <span className={`inline-flex items-center gap-1 text-xs ${cls.danger}`}>
              <WarningAmberRoundedIcon sx={{ fontSize: 14 }} aria-hidden />
              {format(t("hatim_progress_unassigned"), { n: `${summary.unassigned} ${unit}` })}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <SoftBar pct={percent} color={progressColor(percent, dark)} height={6} />
          <span className={`text-2xl font-Default shrink-0 ${cls.heading}`}>
            {completed ? t("hatim_progress_status_completed") : `%${percent}`}
          </span>
        </div>
        <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs mt-2 ${cls.muted}`}>
          <span>
            {t("hatim_progress_completed_label")}:{" "}
            <span className={`font-Medium ${cls.title}`}>
              {summary?.totalCompleted || 0} / {totalLabel} {unit}
            </span>
          </span>
          <span>
            {instance.startDate && instance.endDate
              ? `${shortDate(instance.startDate)} - ${shortDate(instance.endDate)}`
              : ""}
          </span>
        </div>
        {!instance.active ? (
          <div className={`text-xs mt-1 ${cls.warning}`}>{t("hatim_progress_no_active")}</div>
        ) : null}
      </section>

      <section className="flex flex-col lg:flex-1 lg:min-h-0">
        <h4 className={`shrink-0 text-xs uppercase tracking-wide mb-1 ${cls.muted}`}>
          {t("hatim_progress_members_title")} · {members.length}
        </h4>
        {members.length === 0 ? (
          <p className={`text-sm ${cls.muted}`}>{t("hatim_progress_empty")}</p>
        ) : (
          <ul className="tk-stagger flex flex-col lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
            {members.map((m) => {
              const pct = Math.min(100, Math.round(m.percent || 0));
              const done = pct >= 100;
              const assigned = m.assignedAmount || 0;
              const doneAmount = m.completedAmount || 0;
              return (
                <li
                  key={String(m.userId || m.email || `${m.name}-${m.surname}`)}
                  className={`py-3 ${cls.row}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center font-SemiBold text-sm shrink-0 ${cls.avatar}`}>
                      {initials(m)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-base font-SemiBold truncate ${cls.title}`}>
                        {m.name} {m.surname}
                      </div>
                      <div className={`text-xs truncate ${cls.muted}`}>
                        {t("hatim_progress_assigned")}: {assignedLabel(m)}
                      </div>
                    </div>
                    <span
                      className="font-Medium text-base shrink-0"
                      style={{ color: done ? (dark ? "#7BE3A8" : "#2E9E6A") : progressColor(pct, dark) }}
                    >
                      {done ? t("hatim_progress_status_completed") : `%${pct}`}
                    </span>
                  </div>
                  <div className="pl-12 mt-2">
                    <SoftBar pct={pct} color={progressColor(pct, dark)} />
                  </div>
                  <div className={`flex items-center justify-between text-xs mt-1.5 pl-12 ${cls.muted}`}>
                    <span>{doneAmount} / {assigned} {unit}</span>
                    <span>{t("hatim_progress_remaining")}: {Math.max(0, assigned - doneAmount)} {unit}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
